import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider } from './utils/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import PlatformAdminRoute from './components/PlatformAdminRoute';
import Layout from './components/Layout';
import PlatformAdminLayout from './components/PlatformAdminLayout';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import PlatformAdminLoginPage from './pages/PlatformAdminLoginPage';
import RegistrationPage from './pages/RegistrationPage';
import ActivateAccountPage from './pages/ActivateAccountPage';

import DashboardPage from './pages/DashboardPage';
import PIIRecordsPage from './pages/PIIRecordsPage';
import DocumentsPage from './pages/DocumentsPage';
import RevocationRunsPage from './pages/DocumentsPage';
import RevocationRunDetailPage from './pages/RevocationRunDetailPage';

import ComingSoonPage from './pages/ComingSoonPage';
import GroupsPermissionsPage from './pages/GroupsPermissionsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import { SecureDocumentViewerPage } from './components/SecureDocumentViewer';

import ROPAPage from './pages/ROPAPage';
import AuditLogsPage from './pages/AuditLogsPage';
import PlatformPrincipalsPage from './pages/PlatformPrincipalsPage';

import PlatformAdminOnboardingRequestsPage from './pages/PlatformAdminOnboardingRequestsPage';
import PlatformAdminOnboardingRequestDetailPage from './pages/PlatformAdminOnboardingRequestDetailPage';
import MicrosoftSSOCompletePage from './pages/MicrosoftSSOCompletePage';
import MicrosoftIdentitySetupStartPage from './pages/MicrosoftIdentitySetupStartPage';
import IdentitySetupPage from './pages/IdentitySetupPage';

import './App.css';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/platform-admin/login" element={<PlatformAdminLoginPage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/activate/:uidb64/:token" element={<ActivateAccountPage />} />
          <Route path="/sso/microsoft/success" element={<MicrosoftSSOCompletePage />} />
          <Route path="/identity-setup/microsoft" element={<MicrosoftIdentitySetupStartPage />} />

          {/* Platform Admin Portal */}
          <Route
            path="/platform-admin/*"
            element={
              <PlatformAdminRoute>
                <PlatformAdminLayout />
              </PlatformAdminRoute>
            }
          >
            <Route index element={<Navigate to="onboarding-requests" replace />} />
            <Route path="onboarding-requests" element={<PlatformAdminOnboardingRequestsPage />} />
            <Route path="onboarding-requests/:requestId" element={<PlatformAdminOnboardingRequestDetailPage />} />
            <Route path="*" element={<Navigate to="onboarding-requests" replace />} />
          </Route>

          {/* Organization/Admin Private App Routes */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="records" element={<PIIRecordsPage />} />
            <Route path="documents" element={<DocumentsPage />} />

            <Route path="principals" element={<PlatformPrincipalsPage />} />
            <Route path="settings/identity" element={<IdentitySetupPage />} />

            <Route
              path="family-links"
              element={
                <ComingSoonPage
                  title="Employee / Family Links"
                  description="Manage employee and family-member identity relationships across submitted PII bundles."
                />
              }
            />

            <Route
              path="consent-context"
              element={
                <ComingSoonPage
                  title="Consent Context"
                  description="View consent snapshots, consent receipts, signed notice metadata, and linkage health."
                />
              }
            />

            <Route path="access-control/groups" element={<GroupsPermissionsPage />} />
            <Route path="access-control/users" element={<AdminUsersPage />} />

            <Route path="audit-logs" element={<AuditLogsPage />} />

            <Route path="ropa" element={<ROPAPage />} />

            <Route
              path="settings"
              element={
                <ComingSoonPage
                  title="Settings"
                  description="Configure privacy operations, session policy, redaction rules, and platform controls."
                />
              }
            />

            <Route path="revocations/runs" element={<RevocationRunsPage />} />
            <Route path="revocations/runs/:id" element={<RevocationRunDetailPage />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* Standalone full-screen secure document viewer */}
          <Route
            path="/view-document/:documentId"
            element={
              <PrivateRoute>
                <SecureDocumentViewerPage />
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;